import { authStore } from "@/store/auth.js";
import { cloudFunctions } from "@/store/cloudFunctions.js";
import { platformBilling } from "@/scripts/platformBilling.js";
import { getActiveLocale } from "@/locales/index.js";
import {
  startBillingWatch,
  armPendingCheckout,
  resetBillingConfirmation,
} from "@/composables/useBillingConfirmation.js";

// Both endpoints below send `locale`, and both send the APP's own code
// ("en-us", "es-mx", "zh-hk") rather than a Stripe one. Stripe's locale
// vocabulary is different and narrower, and translating here would put an
// allowlist Stripe rejects values from on the untrusted side of the wire. The
// backend (functions/billing/helpers/stripeLocale.py) maps it; anything it
// does not recognize falls back to Stripe's own browser detection, so a stale
// or hostile value costs nothing.
export const stripeUtils = {
  async createBillingSession() {
    try {
      const token = await authStore.getIdToken();
      if (!token) throw new Error("Not authenticated");
      const response = await fetch(`${cloudFunctions.getStripeBillingApi()}`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ locale: getActiveLocale() }),
      });

      if (!response.ok) {
        throw new Error("Failed to create billing session");
      }

      const data = await response.json();
      return data.url;
    } catch (error) {
      console.error("Error creating billing session:", error);
      throw error;
    }
  },

  /**
   * Call createCheckoutSession and return the raw response data.
   * @param {string} lookupKey
   * @param {"web"|"android"|"ios"} platform
   */
  async createCheckout(lookupKey, platform) {
    const token = await authStore.getIdToken();
    if (!token) throw new Error("Not authenticated");
    const response = await fetch(`${cloudFunctions.getStripeCheckoutApi()}`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify({ lookupKey, platform, locale: getActiveLocale() }),
    });

    if (!response.ok) {
      throw new Error("Failed to create checkout or upgrade");
    }

    return response.json();
  },

  /**
   * Begin checkout/upgrade for the current platform.
   * - web: redirect the page to Stripe (unchanged behavior).
   * - android: open Stripe in a Chrome Custom Tab; return handled via App Link.
   * - ios: open Stripe in the user's DEFAULT BROWSER (external Safari/Chrome);
   *   return via universal link or articulearn:// bounce.
   * Native paths arm the RTDB entitlement watcher.
   */
  async startCheckout(lookupKey) {
    const platform = platformBilling.getNativePlatform() || "web";
    const data = await this.createCheckout(lookupKey, platform);

    // Instant upgrade (existing Stripe customer): backend already modified the
    // subscription; no browser needed. The webhook updates billing.lookupKey to
    // the new plan shortly, so watch for that specific plan before declaring
    // success rather than reporting the already-active old one. Web previously
    // redirected straight to ?checkoutSuccess=true and read stale entitlement,
    // flashing the old tier until the user re-navigated.
    if (data.upgradeSuccess) {
      armPendingCheckout(lookupKey);
      startBillingWatch();
      return;
    }

    if (!data.checkoutUrl) {
      // Surfaced to the caller's catch (which shows a toast).
      throw new Error("Checkout URL not received");
    }

    if (platform === "web") {
      // Persist the pending plan so the full-page return from Stripe
      // (success_url → account?checkoutSuccess=true) can wait for the webhook
      // to reflect the purchased plan before showing success.
      armPendingCheckout(lookupKey);
      window.location.href = data.checkoutUrl;
      return;
    }

    // Native: open checkout in a browser and arm the entitlement watcher.
    // Return path: verified https app link or the articulearn:// bounce
    // (App.vue appUrlOpen), plus an app-resume fallback, so the watch timeout
    // measures webhook lag after payment, not the whole payment.
    //
    // iOS MUST use the DEFAULT BROWSER, not an in-app sheet. Guideline
    // 3.1.1's US-storefront carve-out covers "link[ing] out to the default
    // browser" only — build 1.0 (46) was rejected 2026-07-29 for completing
    // purchases inside an SFSafariViewController. No StoreKit entitlement,
    // disclosure sheet, or ExternalPurchaseLink API is required for the
    // link-out itself (injunction-era 3.1.1(a), reconfirmed by that
    // rejection's own wording). Non-US iOS storefronts never reach this
    // path: they are iap-only via platformBilling.getPaymentStrategy.
    //
    // Arm BEFORE launching: the external browser backgrounds the app (the
    // old sheet never did), so iOS may suspend JS between the launch
    // resolving and a post-launch arm — losing the plan identity the return
    // watch needs. A launch failure rolls back below; failure means the
    // browser never opened and the app never left the foreground, so the
    // rollback runs reliably (best-effort beyond that — a stale key at
    // worst produces a dismissible overlay, and the ?plan= return param
    // keeps any watch plan-scoped).
    armPendingCheckout(lookupKey);
    try {
      if (platform === "ios") {
        const { AppLauncher } = await import("@capacitor/app-launcher");
        const { completed } = await AppLauncher.openUrl({ url: data.checkoutUrl });
        // completed=false is UIApplication.open reporting the system refused
        // the URL — a definitive launch failure, same as a rejection.
        if (!completed) {
          throw new Error("Could not open browser for checkout");
        }
      } else {
        const { Browser } = await import("@capacitor/browser");
        await Browser.open({ url: data.checkoutUrl });
      }
    } catch (error) {
      resetBillingConfirmation();
      throw error;
    }
  },
};
